import React, { useEffect, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faGlobe,
  faSpinner,
  faTriangleExclamation,
  faDatabase,
  faClock,
  faArrowRight,
} from '@fortawesome/free-solid-svg-icons';
import StoreHeader from '../../components/StoreHeader';
import Footer from '../../components/Footer';
import { useCurrency } from '../../context/CurrencyContext';
import { formatPrice, pickPrice } from '../../utils/currency';
import { getEsimDetail } from './homeApi';
import './home.css';

// "10240" MB → "10 GB", "512" MB → "512 MB"
const formatData = (mb) => {
  if (mb == null) return '';
  if (mb >= 1024) {
    const gb = mb / 1024;
    return `${Number.isInteger(gb) ? gb : gb.toFixed(1)} GB`;
  }
  return `${mb} MB`;
};

const EsimDetail = () => {
  const { t } = useTranslation();
  const { currency } = useCurrency();
  const { type, id } = useParams();
  const location = useLocation();

  const fallbackName = location.state?.name || decodeURIComponent(id || '');
  const fallbackFlag = location.state?.flag || null;

  const [state, setState] = useState({ loading: true });

  useEffect(() => {
    let cancelled = false;
    setState({ loading: true });
    getEsimDetail(type, id)
      .then((detail) => !cancelled && setState({ detail }))
      .catch((err) => !cancelled && setState({ error: err.message }));
    return () => {
      cancelled = true;
    };
  }, [type, id]);

  const detail = state.detail;
  const name = detail?.name || fallbackName;
  const flag = detail?.flag || fallbackFlag;
  const products = detail?.products || [];
  const planCount = products.reduce((n, p) => n + (p.refills?.length || 0), 0);

  return (
    <div className="home">
      <StoreHeader />

      <section className="home-catalog detail">
        <Link to="/" className="esim-detail-back">
          <FontAwesomeIcon icon={faArrowLeft} />
          {t('home.detail.back')}
        </Link>

        {/* Başlık */}
        <header className="detail-head">
          {flag ? (
            <img className="detail-flag" src={flag} alt="" />
          ) : (
            <span className="detail-flag detail-flag--fallback">
              <FontAwesomeIcon icon={faGlobe} />
            </span>
          )}
          <div>
            <h1 className="detail-title">{name}</h1>
            {!state.loading && !state.error && planCount > 0 && (
              <p className="detail-sub">{t('detail.count', { count: planCount })}</p>
            )}
          </div>
        </header>

        {state.loading && (
          <div className="home-state">
            <FontAwesomeIcon icon={faSpinner} spin size="2x" />
          </div>
        )}

        {state.error && (
          <div className="home-state">
            <FontAwesomeIcon icon={faTriangleExclamation} size="2x" />
            <p>{t('home.error.title')}</p>
          </div>
        )}

        {!state.loading && !state.error && planCount === 0 && (
          <div className="home-state">
            <p>{t('detail.empty')}</p>
          </div>
        )}

        {!state.loading && !state.error && planCount > 0 && (
          <div className="detail-products">
            {products
              .filter((p) => p.refills?.length)
              .map((product) => (
                <div key={product.originalId} className="detail-product">
                  {product.name && (
                    <h2 className="detail-product-name">{product.name}</h2>
                  )}
                  <ul className="plan-list">
                    {product.refills.map((r) => {
                      const price = formatPrice(pickPrice(r, currency), currency);
                      return (
                        <li key={`${product.originalId}-${r.id}`}>
                          <Link
                            className="plan"
                            to={`/esim/plan/${product.originalId}/${r.id}`}
                            state={{ title: name, flag }}
                          >
                            <span className="plan-data">
                              <FontAwesomeIcon icon={faDatabase} />
                              {formatData(r.amountMb)}
                            </span>
                            <span className="plan-validity">
                              <FontAwesomeIcon icon={faClock} />
                              {t('detail.validity', { days: r.amountDays })}
                            </span>
                            {price && <span className="plan-price">{price}</span>}
                            <span className="plan-buy">
                              {t('detail.buy')}
                              <FontAwesomeIcon icon={faArrowRight} />
                            </span>
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}
          </div>
        )}
      </section>

      <Footer />
    </div>
  );
};

export default EsimDetail;
